import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import type {
    DestinationActivity,
    DestinationAttraction,
    DestinationRestaurant,
    DestinationItinerary,
    DestinationPracticalInfo
} from '../types';

// Fallback image for broken URLs - Use a simple gradient placeholder to avoid external API dependencies
// This ensures hotels without Tourvisio images show a clean placeholder instead of potentially broken external URLs
const FALLBACK_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#1a365d"/>
      <stop offset="100%" style="stop-color:#2d3748"/>
    </linearGradient>
  </defs>
  <rect fill="url(#bg)" width="800" height="600"/>
  <g fill="#4a5568" transform="translate(320, 220)">
    <rect x="20" y="80" width="120" height="80" rx="4"/>
    <rect x="0" y="40" width="160" height="40" rx="4"/>
    <rect x="40" y="0" width="80" height="40" rx="4"/>
    <circle cx="80" cy="20" r="8" fill="#718096"/>
  </g>
  <text x="400" y="380" text-anchor="middle" fill="#a0aec0" font-family="system-ui" font-size="18">Hotel Image</text>
</svg>
`);

// Helper function to safely extract localized text from potentially localized objects
// This prevents React Error #31 when an object is rendered directly instead of a string
const getLocalizedText = (value: any, lang: 'en' | 'sv' | 'da' = 'en'): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        // Handle {en, sv, da} translation objects
        if (value[lang] !== undefined) return String(value[lang]);
        if (value.en !== undefined) return String(value.en);
        if (value.sv !== undefined) return String(value.sv);
        if (value.da !== undefined) return String(value.da);
        // Fallback: return first string value found
        for (const key of Object.keys(value)) {
            if (typeof value[key] === 'string') return value[key];
        }
    }
    return String(value);
};

// Helper to safely extract localized array
const getLocalizedArray = (value: any, lang: 'en' | 'sv' | 'da' = 'en'): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(v => getLocalizedText(v, lang));
    if (typeof value === 'object') {
        const arr = value[lang] || value.en || value.sv || value.da;
        if (Array.isArray(arr)) return arr;
    }
    return [];
};

// Helper to strip HTML tags for plain text previews
export const stripHtml = (s: string) => {
    if (!s) return '';
    return s.replace(/<[^>]*>/g, '').trim();
};

// ===== Things To Do Section =====
interface ThingsToDoSectionProps {
    activities: DestinationActivity[];
}

export const ThingsToDoSection: React.FC<ThingsToDoSectionProps> = ({ activities }) => {
    const { language, t } = useLocalization();
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
    const [imageErrors, setImageErrors] = React.useState<Record<string, boolean>>({});
    const [selectedActivity, setSelectedActivity] = React.useState<DestinationActivity | null>(null);

    const categories = [
        { id: 'all', label: { en: 'All', sv: 'Alla', da: 'Alle' }, icon: 'apps' },
        { id: 'adventure', label: { en: 'Adventure', sv: 'Äventyr', da: 'Eventyr' }, icon: 'terrain' },
        { id: 'culture', label: { en: 'Culture', sv: 'Kultur', da: 'Kultur' }, icon: 'account_balance' },
        { id: 'nature', label: { en: 'Nature', sv: 'Natur', da: 'Natur' }, icon: 'park' },
        { id: 'family', label: { en: 'Family', sv: 'Familj', da: 'Familie' }, icon: 'family_restroom' },
        { id: 'food', label: { en: 'Food & Drink', sv: 'Mat & Dryck', da: 'Mad & Drikke' }, icon: 'restaurant' },
    ];

    const filteredActivities = selectedCategory === 'all'
        ? activities
        : activities.filter(a => a.category === selectedCategory);

    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-10">
                    <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                        {language === 'sv' ? 'Upplev' : language === 'da' ? 'Udforsk' : 'Explore'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        {language === 'sv' ? 'Saker att Göra' : language === 'da' ? 'Ting at Gøre' : 'Things to Do'}
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        {language === 'sv'
                            ? 'Upptäck de bästa upplevelserna och aktiviteterna på din destination'
                            : language === 'da' ? 'Oplev de bedste oplevelser og aktiviteter på din destination'
                            : 'Discover the best experiences and activities at your destination'}
                    </p>
                </div>

                {/* Category Filters */}
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === cat.id
                                ? 'bg-primary text-white shadow-lg shadow-primary/25'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            <span className="material-icons-round text-lg">{cat.icon}</span>
                            {getLocalizedText(cat.label, language)}
                        </button>
                    ))}
                </div>

                {/* Activity Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredActivities.map(activity => (
                        <div
                            key={activity.id}
                            className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                        >
                            {/* Image */}
                            <div className="relative h-48 overflow-hidden bg-gray-200">
                                <img
                                    src={imageErrors[activity.id] ? FALLBACK_IMAGE : activity.image}
                                    alt={getLocalizedText(activity.title, language)}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    onError={() => setImageErrors(prev => ({ ...prev, [activity.id]: true }))}
                                />
                                <div className="absolute top-3 left-3">
                                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-bold text-gray-700 rounded-full capitalize">
                                        {activity.category}
                                    </span>
                                </div>
                                {activity.duration && (
                                    <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full">
                                        <span className="material-icons-round text-sm">schedule</span>
                                        {activity.duration}
                                    </div>
                                )}
                            </div>
                            {/* Content */}
                            <div className="p-5">
                                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                                    {getLocalizedText(activity.title, language)}
                                </h3>
                                <div className="text-sm text-gray-600 line-clamp-2 mb-4">
                                    {stripHtml(getLocalizedText(activity.description, language))}
                                </div>
                                <div className="flex items-center justify-between">
                                    {activity.price && (
                                        <span className="text-primary font-bold">{activity.price}</span>
                                    )}
                                    <button
                                        onClick={() => setSelectedActivity(activity)}
                                        className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-primary transition-colors"
                                    >
                                        {language === 'sv' ? 'Läs mer' : language === 'da' ? 'Læs mere' : 'Learn more'}
                                        <span className="material-icons-round text-sm">arrow_forward</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Activity Details Modal */}
            {selectedActivity && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedActivity(null)}
                >
                    <div
                        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-fade-in-up"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header Image */}
                        <div className="relative h-64 overflow-hidden">
                            <img
                                src={imageErrors[selectedActivity.id] ? FALLBACK_IMAGE : selectedActivity.image}
                                alt={getLocalizedText(selectedActivity.title, language)}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <button
                                onClick={() => setSelectedActivity(null)}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <span className="material-icons-round">close</span>
                            </button>
                            <div className="absolute bottom-4 left-6 right-6">
                                <span className="inline-block px-3 py-1 bg-primary text-white text-xs font-bold rounded-full mb-2 capitalize">
                                    {selectedActivity.category}
                                </span>
                                <h3 className="text-2xl font-bold text-white">
                                    {getLocalizedText(selectedActivity.title, language)}
                                </h3>
                            </div>
                        </div>
                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-16rem)]">
                            <div className="text-gray-600 mb-6 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedActivity.description, language) }} />
                            <div className="flex flex-wrap gap-4 mb-6">
                                {selectedActivity.duration && (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <span className="material-icons-round text-primary">schedule</span>
                                        <span className="text-sm font-medium">{selectedActivity.duration}</span>
                                    </div>
                                )}
                                {selectedActivity.price && (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <span className="material-icons-round text-primary">payments</span>
                                        <span className="text-sm font-medium">{selectedActivity.price}</span>
                                    </div>
                                )}
                            </div>
                            {/* AI Insight */}
                            {selectedActivity.aiComment && getLocalizedText(selectedActivity.aiComment, language) && (
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-100">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-icons-round text-indigo-600">auto_awesome</span>
                                        <span className="text-sm font-bold text-indigo-700">
                                            {language === 'sv' ? 'AI Insikt' : language === 'da' ? 'AI Indsigt' : 'AI Insight'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-indigo-900/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedActivity.aiComment, language) }} />
                                </div>
                            )}
                            <button
                                onClick={() => setSelectedActivity(null)}
                                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                            >
                                <span className="material-icons-round">check_circle</span>
                                {language === 'sv' ? 'Stäng' : language === 'da' ? 'Luk' : 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

// ===== Attractions Section =====
interface AttractionsSectionProps {
    attractions: DestinationAttraction[];
}

export const AttractionsSection: React.FC<AttractionsSectionProps> = ({ attractions }) => {
    const { language } = useLocalization();
    const [imageErrors, setImageErrors] = React.useState<Record<string, boolean>>({});
    const [selectedAttraction, setSelectedAttraction] = React.useState<DestinationAttraction | null>(null);

    return (
        <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex items-center gap-3 mb-10">
                    <span className="w-1 h-10 bg-primary rounded-full"></span>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                            {language === 'sv' ? 'Sevärdheter' : language === 'da' ? 'Seværdigheder' : 'Sights & Attractions'}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {language === 'sv' ? 'Ikoniska platser du inte får missa' : language === 'da' ? 'Ikoniske steder du ikke må gå glip af' : 'Iconic places you can\'t miss'}
                        </p>
                    </div>
                </div>

                {/* Horizontal Scroll Grid */}
                <div className="flex overflow-x-auto gap-5 pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
                    {attractions.map(attr => (
                        <div
                            key={attr.id}
                            className="flex-shrink-0 w-72 snap-start group cursor-pointer"
                            onClick={() => setSelectedAttraction(attr)}
                        >
                            <div className="relative h-80 rounded-2xl overflow-hidden bg-gray-200">
                                <img
                                    src={imageErrors[attr.id] ? FALLBACK_IMAGE : attr.image}
                                    alt={getLocalizedText(attr.title, language)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    onError={() => setImageErrors(prev => ({ ...prev, [attr.id]: true }))}
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                {/* Category Badge */}
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full capitalize">
                                        {attr.category}
                                    </span>
                                </div>
                                {/* Content at Bottom */}
                                <div className="absolute bottom-0 left-0 right-0 p-5">
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        {getLocalizedText(attr.title, language)}
                                    </h3>
                                    <div className="text-sm text-white/80 line-clamp-2">
                                        {stripHtml(getLocalizedText(attr.description, language))}
                                    </div>
                                    {attr.entryFee && (
                                        <div className="mt-3 text-sm text-white/90 flex items-center gap-1">
                                            <span className="material-icons-round text-sm">local_activity</span>
                                            {attr.entryFee}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Attraction Details Modal */}
            {selectedAttraction && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedAttraction(null)}
                >
                    <div
                        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-fade-in-up"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header Image */}
                        <div className="relative h-64 overflow-hidden">
                            <img
                                src={imageErrors[selectedAttraction.id] ? FALLBACK_IMAGE : selectedAttraction.image}
                                alt={getLocalizedText(selectedAttraction.title, language)}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <button
                                onClick={() => setSelectedAttraction(null)}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <span className="material-icons-round">close</span>
                            </button>
                            <div className="absolute bottom-4 left-6 right-6">
                                <span className="inline-block px-3 py-1 bg-primary text-white text-xs font-bold rounded-full mb-2 capitalize">
                                    {selectedAttraction.category}
                                </span>
                                <h3 className="text-2xl font-bold text-white">
                                    {getLocalizedText(selectedAttraction.title, language)}
                                </h3>
                            </div>
                        </div>
                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-16rem)]">
                            <div className="text-gray-600 mb-6 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedAttraction.description, language) }} />
                            <div className="flex flex-wrap gap-4 mb-6">
                                {selectedAttraction.entryFee && (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <span className="material-icons-round text-primary">local_activity</span>
                                        <span className="text-sm font-medium">{selectedAttraction.entryFee}</span>
                                    </div>
                                )}
                                {selectedAttraction.address && (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <span className="material-icons-round text-primary">location_on</span>
                                        <span className="text-sm font-medium">{selectedAttraction.address}</span>
                                    </div>
                                )}
                                {selectedAttraction.openingHours && (
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <span className="material-icons-round text-primary">schedule</span>
                                        <span className="text-sm font-medium">{selectedAttraction.openingHours}</span>
                                    </div>
                                )}
                            </div>
                            {/* AI Insight */}
                            {selectedAttraction.aiComment && getLocalizedText(selectedAttraction.aiComment, language) && (
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-100">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-icons-round text-indigo-600">auto_awesome</span>
                                        <span className="text-sm font-bold text-indigo-700">
                                            {language === 'sv' ? 'AI Insikt' : 'AI Insight'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-indigo-900/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedAttraction.aiComment, language) }} />
                                </div>
                            )}
                            <button
                                onClick={() => setSelectedAttraction(null)}
                                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                            >
                                <span className="material-icons-round">check_circle</span>
                                {language === 'sv' ? 'Stäng' : 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

// ===== Itineraries Section =====
interface ItinerariesSectionProps {
    itineraries: DestinationItinerary[];
}

export const ItinerariesSection: React.FC<ItinerariesSectionProps> = ({ itineraries }) => {
    const { language } = useLocalization();
    const [imageErrors, setImageErrors] = React.useState<Record<string, boolean>>({});
    const [selectedItinerary, setSelectedItinerary] = React.useState<DestinationItinerary | null>(null);

    return (
        <section className="py-16 bg-gradient-to-br from-primary/5 to-teal-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-10">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                        <span className="material-icons-round text-sm">route</span>
                        {language === 'sv' ? 'Inspirationer' : language === 'da' ? 'Lad Dig Inspirere' : 'Be Inspired'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        {language === 'sv' ? 'Resplaner' : language === 'da' ? 'Foreslåede Rejseplaner' : 'Suggested Itineraries'}
                    </h2>
                </div>

                {/* Itinerary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {itineraries.map(itin => (
                        <div
                            key={itin.id}
                            className="group relative rounded-2xl overflow-hidden cursor-pointer"
                        >
                            {/* Background Image */}
                            <div className="relative h-96 bg-gray-200">
                                <img
                                    src={imageErrors[itin.id] ? FALLBACK_IMAGE : itin.image}
                                    alt={getLocalizedText(itin.title, language)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    onError={() => setImageErrors(prev => ({ ...prev, [itin.id]: true }))}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                            </div>

                            {/* Days Badge */}
                            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-lg">
                                <span className="material-icons-round text-primary text-lg">calendar_today</span>
                                <span className="font-bold text-gray-900">
                                    {itin.days} {language === 'sv' ? (itin.days === 1 ? 'Dag' : 'Dagar') : language === 'da' ? (itin.days === 1 ? 'Dag' : 'Dage') : (itin.days === 1 ? 'Day' : 'Days')}
                                </span>
                            </div>

                            {/* Content */}
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <h3 className="text-xl font-bold text-white mb-2">
                                    {getLocalizedText(itin.title, language)}
                                </h3>
                                <div className="text-sm text-white/80 mb-4" dangerouslySetInnerHTML={{ __html: getLocalizedText(itin.description, language) }} />
                                {/* Highlights */}
                                <div className="space-y-1">
                                    {getLocalizedArray(itin.highlights, language).slice(0, 3).map((hl, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm text-white/90">
                                            <span className="material-icons-round text-xs text-primary">check_circle</span>
                                            {hl}
                                        </div>
                                    ))}
                                </div>
                                {/* CTA */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedItinerary(itin);
                                    }}
                                    className="mt-4 flex items-center gap-2 text-white font-medium group-hover:text-primary transition-colors"
                                >
                                    {language === 'sv' ? 'Visa resplan' : language === 'da' ? 'Se Rejseplan' : 'View Itinerary'}
                                    <span className="material-icons-round text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Itinerary Details Modal */}
            {selectedItinerary && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedItinerary(null)}
                >
                    <div
                        className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-fade-in-up"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="relative h-56 overflow-hidden">
                            <img
                                src={imageErrors[selectedItinerary.id] ? FALLBACK_IMAGE : selectedItinerary.image}
                                alt={getLocalizedText(selectedItinerary.title, language)}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                            <button
                                onClick={() => setSelectedItinerary(null)}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <span className="material-icons-round">close</span>
                            </button>
                            <div className="absolute bottom-4 left-6 right-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-white text-gray-900 text-xs font-bold rounded-full">
                                        <span className="material-icons-round text-primary text-sm">calendar_today</span>
                                        {selectedItinerary.days} {language === 'sv' ? (selectedItinerary.days === 1 ? 'Dag' : 'Dagar') : (selectedItinerary.days === 1 ? 'Day' : 'Days')}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-white">
                                    {getLocalizedText(selectedItinerary.title, language)}
                                </h3>
                            </div>
                        </div>
                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-14rem)]">
                            <div className="text-gray-600 mb-6 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedItinerary.description, language) }} />

                            {/* Highlights */}
                            <div className="mb-6">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <span className="material-icons-round text-primary">stars</span>
                                    {language === 'sv' ? 'Höjdpunkter' : language === 'da' ? 'Højdepunkter' : 'Highlights'}
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {getLocalizedArray(selectedItinerary.highlights, language).map((hl, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                                            <span className="material-icons-round text-primary text-lg flex-shrink-0">check_circle</span>
                                            <span className="text-sm text-gray-700">{hl}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Day by Day */}
                            {selectedItinerary.days > 1 && (
                                <div className="mb-6">
                                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <span className="material-icons-round text-primary">route</span>
                                        {language === 'sv' ? 'Dag för Dag' : language === 'da' ? 'Dag for Dag' : 'Day by Day'}
                                    </h4>
                                    <div className="space-y-3">
                                        {Array.from({ length: selectedItinerary.days }, (_, i) => (
                                            <div key={i} className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-xl border-l-4 border-primary">
                                                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                                                    {i + 1}
                                                </div>
                                                <div>
                                                    <h5 className="font-bold text-gray-900">
                                                        {language === 'sv' || language === 'da' ? `Dag ${i + 1}` : `Day ${i + 1}`}
                                                    </h5>
                                                    <p className="text-sm text-gray-600">
                                                        {getLocalizedArray(selectedItinerary.highlights, language)[i] || (language === 'sv' ? 'Utforska destinationen' : language === 'da' ? 'Udforsk destinationen' : 'Explore the destination')}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AI Travel Tip */}
                            {selectedItinerary.aiComment && getLocalizedText(selectedItinerary.aiComment, language) && (
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-100">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-icons-round text-indigo-600">auto_awesome</span>
                                        <span className="text-sm font-bold text-indigo-700">
                                            {language === 'sv' ? 'AI Resetips' : language === 'da' ? 'AI Rejsetip' : 'AI Travel Tip'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-indigo-900/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: getLocalizedText(selectedItinerary.aiComment, language) }} />
                                </div>
                            )}

                            <button
                                onClick={() => setSelectedItinerary(null)}
                                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                            >
                                <span className="material-icons-round">check_circle</span>
                                {language === 'sv' ? 'Stäng' : 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

// ===== Restaurants Section =====
interface RestaurantsSectionProps {
    restaurants: DestinationRestaurant[];
}

export const RestaurantsSection: React.FC<RestaurantsSectionProps> = ({ restaurants }) => {
    const { language } = useLocalization();
    const navigate = useNavigate();

    const getSlug = (rest: DestinationRestaurant) => {
        return rest.slug || rest.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    };

    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                            <span className="material-icons-round text-amber-600 text-2xl">restaurant</span>
                        </div>
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {language === 'sv' ? 'Var man Äter' : language === 'da' ? 'Hvor man Spiser' : 'Where to Eat'}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {language === 'sv' ? 'Upptäck lokala smaker' : language === 'da' ? 'Oplev lokale smage' : 'Discover local flavors'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Restaurant Cards - Horizontal Scroll */}
                <div className="flex overflow-x-auto gap-5 pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
                    {restaurants.map(rest => (
                        <div
                            key={rest.id}
                            className="flex-shrink-0 w-80 snap-start bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group cursor-pointer hover:-translate-y-1"
                            onClick={() => navigate(`/restaurants/${getSlug(rest)}`)}
                        >
                            {/* Image */}
                            <div className="relative h-44 overflow-hidden">
                                <img
                                    src={rest.image}
                                    alt={rest.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-3 right-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-md text-sm font-bold">
                                    {rest.priceRange}
                                </div>
                                {rest.rating && (
                                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-md">
                                        <span className="material-icons-round text-amber-500 text-sm">star</span>
                                        <span className="text-sm font-bold text-gray-900">{rest.rating}</span>
                                    </div>
                                )}
                            </div>
                            {/* Content */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors">{rest.name}</h3>
                                    {rest.rating && (
                                        <div className="flex items-center gap-1 text-amber-500">
                                            <span className="material-icons-round text-sm">star</span>
                                            <span className="text-sm font-bold">{rest.rating}</span>
                                        </div>
                                    )}
                                </div>
                                <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded mb-2">
                                    {rest.cuisine}
                                </span>
                                <div className="text-sm text-gray-600 line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: getLocalizedText(rest.description, language) }} />
                                {(rest as any).tripAdvisorUrl && (
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <span className="text-sm">🦉</span>
                                        <span className="text-xs text-green-700 font-medium">TripAdvisor</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                    {language === 'sv' ? 'Visa mer' : language === 'da' ? 'Vis detaljer' : 'View details'}
                                    <span className="material-icons-round text-sm">arrow_forward</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ===== Practical Info Section =====
interface PracticalInfoSectionProps {
    info: DestinationPracticalInfo;
}

export const PracticalInfoSection: React.FC<PracticalInfoSectionProps> = ({ info }) => {
    const { language } = useLocalization();

    // Safely extract currency display — handle both {code, name} object and localized {en, sv} string
    const getCurrencyDisplay = () => {
        if (!info.currency) return '';
        if (typeof info.currency === 'string') return info.currency;
        if (info.currency.code && info.currency.name) {
            return `${info.currency.code} (${getLocalizedText(info.currency.name, language)})`;
        }
        // Localized string like {en: "Euro (€)", sv: "Euro (€)"}
        return getLocalizedText(info.currency, language);
    };

    const t = (en: string, sv: string, da: string) => language === 'da' ? da : language === 'sv' ? sv : en;

    const infoCards = [
        { icon: 'payments', title: t('Currency', 'Valuta', 'Valuta'), value: getCurrencyDisplay() },
        { icon: 'translate', title: t('Language', 'Språk', 'Sprog'), value: getLocalizedText(info.language, language) },
        { icon: 'schedule', title: t('Timezone', 'Tidszon', 'Tidszone'), value: getLocalizedText(info.timezone, language) },
        { icon: 'wb_sunny', title: t('Best Time', 'Bästa Tiden', 'Bedste Tid'), value: getLocalizedText(info.bestTimeToVisit, language) },
        { icon: 'electrical_services', title: t('Electricity', 'Elektricitet', 'Elektricitet'), value: getLocalizedText(info.electricity, language) },
    ];

    // Safely handle transport — could be an array of {icon, title, description} objects,
    // a localized string {en, sv}, or a plain string
    const renderTransport = () => {
        if (!info.transport) return null;

        // If it's an array of transport objects, render as cards
        if (Array.isArray(info.transport)) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {info.transport.map((t: any, i: number) => (
                        <div key={i} className="bg-gray-800/30 rounded-xl p-5 flex gap-4">
                            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="material-icons-round text-primary">{t.icon || 'directions'}</span>
                            </div>
                            <div>
                                <h4 className="font-bold text-white mb-1">{getLocalizedText(t.title, language)}</h4>
                                <p className="text-sm text-gray-400">{getLocalizedText(t.description, language)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // If it's a localized string or plain string, render as a single info block
        const transportText = getLocalizedText(info.transport, language);
        if (transportText) {
            return (
                <div className="bg-gray-800/30 rounded-xl p-5 flex gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="material-icons-round text-primary">directions</span>
                    </div>
                    <div>
                        <h4 className="font-bold text-white mb-1">
                            {t('Transport Info', 'Transportinfo', 'Transportinfo')}
                        </h4>
                        <div className="text-sm text-gray-400 prose-sm prose-invert" dangerouslySetInnerHTML={{ __html: transportText }} />
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <section className="py-16 bg-gray-900 text-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        {t('Practical Information', 'Praktisk Information', 'Praktisk Information')}
                    </h2>
                    <p className="text-gray-400 max-w-xl mx-auto">
                        {t('Everything you need to know before you travel', 'Allt du behöver veta innan du reser', 'Alt du skal vide, før du rejser')}
                    </p>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                    {infoCards.filter(c => c.value).map((card, i) => (
                        <div key={i} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700/50">
                            <span className="material-icons-round text-primary text-2xl mb-3">{card.icon}</span>
                            <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-1">{card.title}</h4>
                            <div className="text-white font-medium text-sm break-words [&_p]:mb-0 [&_span]:inline" dangerouslySetInnerHTML={{ __html: String(card.value) }} />
                        </div>
                    ))}
                </div>

                {/* Transport Section */}
                <div className="mb-12">
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <span className="material-icons-round text-primary">directions</span>
                        {t('Getting Around', 'Transport', 'Transport')}
                    </h3>
                    {renderTransport()}
                </div>

                {/* Visa Info */}
                {info.visaInfo || info.visa ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 mb-8">
                        <div className="flex gap-4">
                            <span className="material-icons-round text-amber-400 text-2xl">info</span>
                            <div>
                                <h4 className="font-bold text-amber-400 mb-1">
                                    {t('Visa Requirements', 'Visumkrav', 'Visumkrav')}
                                </h4>
                                <div className="text-gray-300 text-sm prose-sm prose-invert" dangerouslySetInnerHTML={{ __html: getLocalizedText(info.visaInfo || info.visa, language) as string }} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Health Info */}
                {info.health && getLocalizedText(info.health, language) ? (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-8">
                        <div className="flex gap-4">
                            <span className="material-icons-round text-emerald-400 text-2xl">health_and_safety</span>
                            <div>
                                <h4 className="font-bold text-emerald-400 mb-1">
                                    {t('Health Information', 'Hälsoinformation', 'Sundhedsinformation')}
                                </h4>
                                <div className="text-gray-300 text-sm prose-sm prose-invert" dangerouslySetInnerHTML={{ __html: getLocalizedText(info.health, language) as string }} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Emergency Info */}
                {info.emergency && getLocalizedText(info.emergency, language) ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 mb-8">
                        <div className="flex gap-4">
                            <span className="material-icons-round text-red-400 text-2xl">emergency</span>
                            <div>
                                <h4 className="font-bold text-red-400 mb-1">
                                    {t('Emergency Info', 'Nödinformation', 'Nødoplysninger')}
                                </h4>
                                <div className="text-gray-300 text-sm prose-sm prose-invert" dangerouslySetInnerHTML={{ __html: getLocalizedText(info.emergency, language) as string }} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Tips */}
                <div>
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <span className="material-icons-round text-primary">tips_and_updates</span>
                        {t('Traveler Tips', 'Resenärstips', 'Rejsetips')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {getLocalizedArray(info.tips, language).map((tip, i) => (
                            <div key={i} className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
                                <span className="material-icons-round text-primary text-lg">check_circle</span>
                                <span className="text-sm text-gray-300">{tip}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};


// ===== Hotels Section (API Hotels) =====
interface HotelDisplay {
    id: string;
    name: string;
    imageUrl: string;
    price: number | null;
    stars: number;
    city: string;
    board?: string;
    // Allow pass-through of additional Tourvisio data (offers, searchId, etc.)
    [key: string]: any;
}

interface HotelsSectionProps {
    hotels: HotelDisplay[];
    loading: boolean;
    destinationName: string;
    onHotelClick: (hotel: HotelDisplay) => void;
}

export const HotelsSection: React.FC<HotelsSectionProps> = ({ hotels, loading, destinationName, onHotelClick }) => {
    const { language, currency } = useLocalization();
    const currencySymbol = currency === 'SEK' || currency === 'DKK' ? 'kr' : '€';

    if (!loading && hotels.length === 0) return null;

    return (
        <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-primary/5">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-primary to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {language === 'sv' ? `Hotell i ${destinationName}` : language === 'da' ? `Hoteller i ${destinationName}` : `Hotels in ${destinationName}`}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {language === 'sv' ? 'Boka direkt med bästa pris' : language === 'da' ? 'Book direkte med de bedste priser' : 'Book directly with the best rates'}
                            </p>
                        </div>
                    </div>
                    {hotels.length > 0 && (
                        <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary text-sm font-bold rounded-full">
                            <span className="material-icons-round text-sm">verified</span>
                            {hotels.length} {language === 'sv' ? 'tillgängliga' : language === 'da' ? 'tilgængelige' : 'available'}
                        </span>
                    )}
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-md animate-pulse">
                                <div className="h-48 bg-gray-200"></div>
                                <div className="p-4 space-y-3">
                                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {hotels.slice(0, 8).map(hotel => (
                            <div
                                key={hotel.id}
                                onClick={() => onHotelClick(hotel)}
                                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:-translate-y-1"
                            >
                                {/* Image */}
                                <div className="relative h-48 overflow-hidden bg-gray-200">
                                    <img
                                        src={hotel.imageUrl || FALLBACK_IMAGE}
                                        alt={hotel.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                                    />
                                    {/* Stars Badge */}
                                    {hotel.stars > 0 && (
                                        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full">
                                            {[...Array(Math.max(0, Math.min(5, Math.floor(hotel.stars || 0))))].map((_, i) => (
                                                <span key={i} className="material-icons-round text-amber-400 text-sm">star</span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Board Badge */}
                                    {hotel.board && (
                                        <div className="absolute bottom-3 left-3 px-2 py-1 bg-primary text-white text-xs font-bold rounded-full">
                                            {hotel.board}
                                        </div>
                                    )}
                                </div>
                                {/* Content */}
                                <div className="p-4">
                                    <h3 className="font-bold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                        {hotel.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 mb-3">
                                        <span className="material-icons-round text-xs align-middle mr-1">location_on</span>
                                        {hotel.city}
                                    </p>
                                    {hotel.price ? (
                                        <div className="flex items-end gap-1">
                                            <span className="text-2xl font-bold text-primary">
                                                {Math.round(hotel.price).toLocaleString('sv-SE')}
                                            </span>
                                            <span className="text-sm text-gray-500 mb-0.5">
                                                {currencySymbol}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">
                                            {language === 'sv' ? 'Pris på förfrågan' : language === 'da' ? 'Pris på forespørgsel' : 'Price on request'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};


// ===== Tour Packages Section (Holiday Packages with Flights) =====
// Uses real HolidayPackage type from types.ts — includes offers with flight data

interface TourPackagesSectionProps {
    packages: any[]; // HolidayPackage[] from tourvisioService
    loading: boolean;
    destinationName: string;
    currency: string;
    onPackageClick: (pkg: any) => void;
}

export const TourPackagesSection: React.FC<TourPackagesSectionProps> = ({ packages, loading, destinationName, currency, onPackageClick }) => {
    const { language } = useLocalization();

    if (!loading && packages.length === 0) return null;

    const formatPrice = (price: number) => {
        return Math.round(price).toLocaleString('sv-SE');
    };

    const currencySymbol = currency === 'SEK' || currency === 'DKK' ? 'kr' : '€';

    // Extract best offer from package
    const getBestOffer = (pkg: any) => {
        if (!pkg.offers || pkg.offers.length === 0) return null;
        return pkg.offers[0]; // Already sorted by price from API
    };

    // Format flight info from offer
    const getFlightInfo = (offer: any) => {
        if (!offer?.outboundFlight) return null;
        const f = offer.outboundFlight;
        return {
            airline: f.airline || '',
            flightNumber: f.flightNumber || '',
            departure: f.departureAirport || '',
            arrival: f.arrivalAirport || '',
            departureTime: f.departureTime || '',
            arrivalTime: f.arrivalTime || '',
            duration: f.duration || '',
        };
    };

    return (
        <section className="py-16 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                            <span className="material-icons-round text-white text-2xl">flight_takeoff</span>
                        </div>
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {language === 'sv'
                                    ? `Paketresor till ${destinationName}`
                                    : language === 'da'
                                    ? `Pakkerejser til ${destinationName}`
                                    : `Holiday Packages to ${destinationName}`}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {language === 'sv'
                                    ? 'Stockholm → ' + destinationName + ' · Flyg + hotell · 10 sep 2026 · 7 nätter · 2 pers'
                                    : language === 'da'
                                    ? 'Stockholm → ' + destinationName + ' · Fly + hotel · 10. sep 2026 · 7 nætter · 2 pers'
                                    : 'Stockholm → ' + destinationName + ' · Flight + hotel · Sep 10, 2026 · 7 nights · 2 pax'}
                            </p>
                        </div>
                    </div>
                    {packages.length > 0 && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 text-sm font-bold rounded-full">
                            <span className="material-icons-round text-sm">local_offer</span>
                            {packages.length} {language === 'sv' ? 'erbjudanden' : language === 'da' ? 'tilbud' : 'offers'}
                        </span>
                    )}
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-md animate-pulse">
                                <div className="h-48 bg-gray-200"></div>
                                <div className="p-5 space-y-3">
                                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    <div className="h-10 bg-gray-200 rounded w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {packages.map((pkg, idx) => {
                            const bestOffer = getBestOffer(pkg);

                            return (
                                <div
                                    key={pkg.id || idx}
                                    onClick={() => onPackageClick(pkg)}
                                    className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100 hover:-translate-y-1"
                                >
                                    {/* Image */}
                                    <div className="relative h-48 overflow-hidden bg-gray-200">
                                        <img
                                            src={pkg.imageUrl || pkg.thumbnailUrl || FALLBACK_IMAGE}
                                            alt={pkg.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                                        {/* Stars Badge */}
                                        {pkg.rating > 0 && (
                                            <div className="absolute top-3 left-3 flex items-center gap-0.5 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full shadow-sm">
                                                {[...Array(Math.max(0, Math.min(5, Math.floor(pkg.rating || 0))))].map((_: any, i: number) => (
                                                    <span key={i} className="material-icons-round text-amber-400" style={{ fontSize: '14px' }}>star</span>
                                                ))}
                                            </div>
                                        )}



                                        {/* Hotel Name Overlay */}
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <h3 className="font-bold text-white text-lg line-clamp-2 drop-shadow-lg">
                                                {pkg.name}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-4">

                                        {/* Details row */}
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
                                            <span className="flex items-center gap-1">
                                                <span className="material-icons-round" style={{ fontSize: '14px' }}>location_on</span>
                                                {pkg.location?.city || destinationName}
                                            </span>
                                            {bestOffer?.board && bestOffer.board !== 'N/A' && (
                                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                                    {bestOffer.board}
                                                </span>
                                            )}
                                            {bestOffer?.roomType && bestOffer.roomType !== 'N/A' && (
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium truncate max-w-[140px]">
                                                    {bestOffer.roomType}
                                                </span>
                                            )}
                                            {bestOffer?.night && (
                                                <span className="flex items-center gap-1">
                                                    <span className="material-icons-round" style={{ fontSize: '14px' }}>dark_mode</span>
                                                    {bestOffer.night} {language === 'sv' ? 'nätter' : language === 'da' ? 'nætter' : 'nights'}
                                                </span>
                                            )}
                                        </div>

                                        {/* Price & CTA */}
                                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                            <div>
                                                <span className="text-[10px] text-gray-400 block uppercase tracking-wider">
                                                    {language === 'sv' ? 'Totalpris fr.' : language === 'da' ? 'Totalpris fra' : 'Total from'}
                                                </span>
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                                        {formatPrice(pkg.totalPrice || bestOffer?.price || 0)}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-500">{currencySymbol}</span>
                                                </div>
                                            </div>
                                            <button className="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors shadow-md shadow-indigo-600/25">
                                                {language === 'sv' ? 'Visa' : language === 'da' ? 'Vis' : 'View'}
                                                <span className="material-icons-round text-sm">arrow_forward</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};


// ===== Premium Hotels Section (Hotel-Only, Top-Rated) =====
interface PremiumHotelsSectionProps {
    hotels: HotelDisplay[];
    loading: boolean;
    destinationName: string;
    currency: string;
    onHotelClick: (hotel: HotelDisplay) => void;
}

export const PremiumHotelsSection: React.FC<PremiumHotelsSectionProps> = ({ hotels, loading, destinationName, currency, onHotelClick }) => {
    const { language } = useLocalization();

    if (!loading && hotels.length === 0) return null;

    const currencySymbol = currency === 'SEK' || currency === 'DKK' ? 'kr' : '€';

    return (
        <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-orange-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                            <span className="material-icons-round text-white text-2xl">star</span>
                        </div>
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {language === 'sv'
                                    ? `Premiumhotell i ${destinationName}`
                                    : `Premium Hotels in ${destinationName}`}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {language === 'sv'
                                    ? 'Utan flyg – boka bara hotellet'
                                    : 'Without flights – book hotel only'}
                            </p>
                        </div>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-bold rounded-full">
                        <span className="material-icons-round text-sm">star</span>
                        {language === 'sv' ? 'Toppklassat' : 'Top Rated'}
                    </span>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-md animate-pulse">
                                <div className="h-52 bg-gray-200"></div>
                                <div className="p-5 space-y-3">
                                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {hotels.map((hotel, idx) => (
                            <div
                                key={hotel.id || idx}
                                onClick={() => onHotelClick(hotel)}
                                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100 hover:-translate-y-1"
                            >
                                {/* Image */}
                                <div className="relative h-52 overflow-hidden bg-gray-200">
                                    <img
                                        src={hotel.imageUrl || FALLBACK_IMAGE}
                                        alt={hotel.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                    {/* Stars */}
                                    {hotel.stars > 0 && (
                                        <div className="absolute top-3 left-3 flex items-center gap-0.5 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full shadow-sm">
                                            {[...Array(Math.max(0, Math.min(5, Math.floor(hotel.stars || 0))))].map((_, i) => (
                                                <span key={i} className="material-icons-round text-amber-400" style={{ fontSize: '14px' }}>star</span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Hotel Only Badge */}
                                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-amber-500 text-white text-xs font-bold rounded-full shadow-lg">
                                        <span className="material-icons-round" style={{ fontSize: '14px' }}>hotel</span>
                                        {language === 'sv' ? 'Bara hotell' : 'Hotel only'}
                                    </div>

                                    {hotel.board && (
                                        <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                                            {hotel.board}
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-5">
                                    <h3 className="font-bold text-gray-900 text-lg line-clamp-2 mb-2 group-hover:text-amber-600 transition-colors">
                                        {hotel.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">
                                        <span className="material-icons-round text-xs">location_on</span>
                                        {hotel.city || destinationName}
                                    </p>

                                    {/* Price */}
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                        {hotel.price ? (
                                            <div>
                                                <span className="text-xs text-gray-400 block">
                                                    {language === 'sv' ? 'Per natt fr.' : 'Per night from'}
                                                </span>
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-2xl font-extrabold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                                        {Math.round(hotel.price).toLocaleString('sv-SE')}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-500">{currencySymbol}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">
                                                {language === 'sv' ? 'Pris på förfrågan' : 'Price on request'}
                                            </span>
                                        )}
                                        <button className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-xl transition-colors shadow-md shadow-amber-500/25">
                                            {language === 'sv' ? 'Visa' : 'View'}
                                            <span className="material-icons-round text-sm">arrow_forward</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

// ===== Airport Info Section (Accordion Widget) =====
interface AirportInfoSectionProps {
    info: {
        name?: any;
        code?: string;
        content?: any;
        isActive?: boolean;
    };
}

// Parse HTML content into accordion sections based on headings (h2/h3 with emoji prefix)
// Parse HTML content into accordion sections based on headings (h2-h6 with emoji prefix, or strong tags acting as headings)
const parseAccordionSections = (html: string): { icon: string; title: string; body: string }[] => {
    if (!html) return [];
    
    const sections: { icon: string; title: string; bodyHtml: string }[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    let currentSection: { icon: string; title: string; bodyHtml: string } | null = null;
    
    Array.from(doc.body.childNodes).forEach(node => {
        let isHeading = false;
        let headingText = '';
        
        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            // Native heading headers
            if (/^H[2-6]$/i.test(el.tagName)) {
                isHeading = true;
                headingText = el.textContent || '';
            } 
            // Simulated headers (paragraph with strong/b ONLY)
            else if (el.tagName === 'P' || el.tagName === 'DIV') {
                const text = el.textContent?.trim() || '';
                const strongElements = el.querySelectorAll('strong, b');
                if (strongElements.length > 0) {
                    // Combine all text from strong tags
                    const strongText = Array.from(strongElements).map(s => s.textContent || '').join(' ').trim();
                    // If the strong text constitutes almost all of the paragraph's text (allowing for emojis/spaces)
                    if (strongText.length > 0 && text.length <= strongText.length + 10) {
                        isHeading = true;
                        headingText = text; // use full text to preserve emojis
                    }
                }
            }
        }
        
        if (isHeading && headingText.trim()) {
            // Start new section
            const emojiMatch = headingText.match(/^([\p{Emoji}\u200d\uFE0F]+)\s*/u);
            const icon = emojiMatch ? emojiMatch[1] : '✈️';
            const cleanTitle = emojiMatch ? headingText.slice(emojiMatch[0].length).trim() : headingText.trim();
            
            // Only push previous section if it had content and we are starting a new one
            if (currentSection) {
                // Clean up trailing breaks/empty paragraphs
                currentSection.bodyHtml = currentSection.bodyHtml.replace(/^(?:<br\s*\/?>|\s)+|(?:<br\s*\/?>|\s)+$/gi, '');
                sections.push(currentSection);
            }
            
            currentSection = {
                icon,
                title: cleanTitle || 'Info',
                bodyHtml: ''
            };
        } else if (currentSection) {
            // Append node to current section's body
            const temp = document.createElement('div');
            temp.appendChild(node.cloneNode(true));
            currentSection.bodyHtml += temp.innerHTML;
        }
    });
    
    if (currentSection) {
        currentSection.bodyHtml = currentSection.bodyHtml.replace(/^(?:<br\s*\/?>|\s)+|(?:<br\s*\/?>|\s)+$/gi, '');
        sections.push(currentSection);
    }
    
    return sections.map(s => ({
        icon: s.icon,
        title: s.title,
        body: s.bodyHtml
    }));
};

export const AirportInfoSection: React.FC<AirportInfoSectionProps> = ({ info }) => {
    const { language } = useLocalization();
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    if (!info || info.isActive === false) return null;

    const name = getLocalizedText(info.name, language);
    const content = getLocalizedText(info.content, language);

    if (!name && !content) return null;

    const accordionSections = parseAccordionSections(content);
    const hasAccordion = accordionSections.length > 0;

    const toggleSection = (idx: number) => {
        setOpenIndex(prev => prev === idx ? null : idx);
    };

    return (
        <section className="relative py-16 overflow-hidden bg-white text-gray-900 border-t border-gray-100">
            {/* === LARGE Airplane Animation === */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {/* Primary large airplane — detailed commercial jet side-profile, flying left-to-right */}
                <img
                    src="/assets/images/realistic_airplane.png"
                    alt="Airplane"
                    className="absolute opacity-[0.10] animate-[airplaneFly_22s_linear_infinite]"
                    style={{ top: '15%', left: '-800px', width: '800px', objectFit: 'contain', mixBlendMode: 'multiply' }}
                />

                {/* Secondary smaller airplane — further away, different altitude */}
                <img
                    src="/assets/images/realistic_airplane.png"
                    alt="Airplane"
                    className="absolute opacity-[0.05] animate-[airplaneFly2_32s_linear_infinite_10s]"
                    style={{ top: '55%', left: '-500px', width: '450px', objectFit: 'contain', mixBlendMode: 'multiply' }}
                />

                {/* Ambient glow */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-50 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-50/50 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/3"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="max-w-4xl mx-auto">
                    {/* Compact Widget Header */}
                    <div className="flex items-center justify-between gap-4 mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-sky-500/25 shrink-0">
                                <span className="material-icons-round text-white text-2xl">flight</span>
                            </div>
                            <div>
                                <h2 className="text-2xl md:text-3xl font-bold leading-tight text-gray-900">
                                    {name || (language === 'sv' ? 'Flygplatsinformation' : language === 'da' ? 'Lufthavnsinformation' : 'Airport Information')}
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {language === 'sv' ? 'Flygplats & Transport' : language === 'da' ? 'Lufthavn & Transport' : 'Airport & Transport'}
                                </p>
                            </div>
                        </div>
                        {info.code && (
                            <div className="bg-white border border-gray-200 shadow-sm rounded-xl px-5 py-3 flex flex-col items-center shrink-0">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                    IATA
                                </span>
                                <span className="text-2xl font-black text-gray-900 tracking-[0.2em] leading-none mt-1">
                                    {info.code}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Accordion Sections */}
                    {hasAccordion ? (
                        <div className="space-y-2">
                            {accordionSections.map((section, idx) => {
                                const isOpen = openIndex === idx;
                                return (
                                    <div
                                        key={idx}
                                        className={`rounded-2xl border transition-all duration-300 ${
                                            isOpen
                                                ? 'bg-sky-50/50 border-sky-100 shadow-lg shadow-sky-500/5'
                                                : 'bg-gray-50/50 border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                                        }`}
                                    >
                                        <button
                                            onClick={() => toggleSection(idx)}
                                            className="w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer group"
                                        >
                                            <span className="text-xl shrink-0">{section.icon}</span>
                                            <span className={`flex-1 font-semibold text-sm md:text-base transition-colors ${
                                                isOpen ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                                            }`}>
                                                {section.title}
                                            </span>
                                            <span className={`material-icons-round text-gray-400 text-xl transition-transform duration-300 ${
                                                isOpen ? 'rotate-180 text-sky-500' : ''
                                            }`}>
                                                expand_more
                                            </span>
                                        </button>
                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                                isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                        >
                                            <div className="px-5 pb-5 pt-1">
                                                <div className="border-t border-gray-100 pt-4">
                                                    <div
                                                        className="prose prose-sm max-w-none
                                                                   prose-p:text-gray-600 prose-p:leading-relaxed prose-p:my-2
                                                                   prose-li:text-gray-600 prose-ul:list-disc prose-ul:pl-4 prose-ul:my-2
                                                                   prose-strong:text-gray-900 marker:text-sky-500"
                                                        dangerouslySetInnerHTML={{ __html: section.body }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (content || '').trim() ? (
                        /* Fallback: if no headings found, render content in a single compact card */
                        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                            <div
                                className="prose prose-sm max-w-none
                                           prose-p:text-gray-600 prose-p:leading-relaxed
                                           prose-li:text-gray-600 prose-ul:list-disc prose-ul:pl-4
                                           marker:text-sky-500"
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Airplane animation keyframes */}
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes airplaneFly {
                    0% { transform: translateX(0) translateY(0); opacity: 0; }
                    3% { opacity: 0.07; }
                    90% { opacity: 0.07; }
                    100% { transform: translateX(calc(100vw + 900px)) translateY(-5vh); opacity: 0; }
                }
                @keyframes airplaneFly2 {
                    0% { transform: translateX(0) translateY(0); opacity: 0; }
                    5% { opacity: 0.04; }
                    88% { opacity: 0.04; }
                    100% { transform: translateX(calc(100vw + 700px)) translateY(-3vh); opacity: 0; }
                }
            `}} />
        </section>
    );
};

// Export all sections
export default {
    ThingsToDoSection,
    AttractionsSection,
    ItinerariesSection,
    RestaurantsSection,
    PracticalInfoSection,
    HotelsSection,
    TourPackagesSection,
    PremiumHotelsSection,
    AirportInfoSection
};

